import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Button, Chip,
} from '@mui/material';
import {
  Lock, Shield, VerifiedUser, Security, Https, ManageAccounts,
  Storage, FilterAlt, BugReport, Policy, CheckCircle, ArrowForward,
  Fingerprint, NetworkCheck, Backup, MonitorHeart, Key, GppGood,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const layers = [
  {
    icon: <Https sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Encryption Everywhere',
    desc: 'All data in transit is protected by TLS 1.3. All data at rest — emails, files, databases — is encrypted with AES-256. Your communications are unreadable to anyone without your credentials.',
    points: ['TLS 1.3 for all SMTP, IMAP, and HTTPS connections', 'AES-256 encryption for stored email data and files', 'Encrypted PostgreSQL and MongoDB volumes', 'End-to-end encrypted backup archives'],
  },
  {
    icon: <ManageAccounts sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Account Security',
    desc: 'Multi-layered authentication keeps unauthorized users out, even if passwords are compromised. CruvzMail supports TOTP-based two-factor authentication across all accounts.',
    points: ['TOTP two-factor authentication (Google Authenticator, Authy)', 'Short-lived JWT access tokens (15-minute expiry)', '7-day refresh tokens stored in Redis with revocation support', 'Admin-enforced 2FA policies organization-wide'],
  },
  {
    icon: <FilterAlt sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Anti-Spam & Anti-Phishing',
    desc: 'Rspamd powers our multi-layer spam detection, cross-referenced with real-time blocklists and AI-assisted scoring. Phishing and malicious URLs are detected and blocked before reaching inboxes.',
    points: ['Rspamd with fuzzy hashing and neural network scoring', 'Real-time blocklist checks (Spamhaus, SURBL, Barracuda)', 'SPF, DKIM, and DMARC validation on every inbound message', 'Quarantine management with admin review dashboard'],
  },
  {
    icon: <BugReport sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Malware Protection',
    desc: 'ClamAV scans every attachment and file uploaded to DriveX in real time. Infected files are quarantined automatically and the user and administrator are alerted immediately.',
    points: ['ClamAV real-time scanning on all email attachments', 'DriveX file scanning before storage and before download', 'Automatic quarantine of detected malware', 'Blocked file type enforcement (executables, dangerous scripts)'],
  },
  {
    icon: <VerifiedUser sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Email Authentication',
    desc: 'CruvzMail enforces industry-standard email authentication for all outgoing mail, protecting your domain from spoofing and dramatically improving deliverability.',
    points: ['OpenDKIM per-domain signing (RSA-2048)', 'SPF hard-fail (-all) on all managed domains', 'DMARC at p=quarantine, pct=100 out of the box', 'MTA-STS policy published for SMTP downgrade protection'],
  },
  {
    icon: <NetworkCheck sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Network & Infrastructure',
    desc: 'All traffic enters through HAProxy with Web Application Firewall protection. Internal services are network-segmented — databases are not accessible from the public internet.',
    points: ['HAProxy load balancer with SSL termination', 'Full network segmentation between application and database VMs', 'Rate limiting on SMTP, API, and login endpoints', 'DDoS protection at the load balancer layer'],
  },
  {
    icon: <MonitorHeart sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Security Monitoring',
    desc: 'All security events are logged, timestamped, and retained for 180 days in compliance with CERT-In guidelines. Anomalous login patterns and velocity spikes trigger automated alerts.',
    points: ['180-day security audit log retention (CERT-In compliant)', 'Failed login and brute-force detection', 'Unusual sending pattern alerts (velocity detection)', 'Admin activity log — every admin action is recorded'],
  },
  {
    icon: <Backup sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Backup & Recovery',
    desc: 'Automated encrypted backups run daily across all databases and file storage. Point-in-time recovery is available for PostgreSQL. Disaster recovery procedures are tested quarterly.',
    points: ['Daily automated encrypted database backups', 'PostgreSQL point-in-time recovery', '30-day backup retention with secure off-volume storage', 'Tested disaster recovery runbooks'],
  },
  {
    icon: <Policy sx={{ fontSize: 32, color: '#dc2626' }} />,
    title: 'Compliance',
    desc: 'CruvzMail is designed to meet the obligations of Indian enterprises under the DPDP Act 2023, IT Act 2000, and CERT-In guidelines. Our infrastructure and policies are documented and audit-ready.',
    points: ['DPDP Act 2023 (Digital Personal Data Protection)', 'CERT-In incident reporting and log retention requirements', 'IT Act 2000 data protection provisions', 'GDPR-compatible data processing agreement available'],
  },
];

const deliverability = [
  { label: 'SPF', desc: 'Hard-fail -all' },
  { label: 'DKIM', desc: 'RSA-2048 per domain' },
  { label: 'DMARC', desc: 'p=quarantine pct=100' },
  { label: 'MTA-STS', desc: 'SMTP downgrade protection' },
  { label: 'DANE', desc: 'DNS-based auth support' },
  { label: 'TLS 1.3', desc: 'All mail transport' },
];

const certifications = [
  { icon: <GppGood sx={{ fontSize: 22, color: '#15803d' }} />, label: 'DPDP Act 2023', color: '#dcfce7', textColor: '#15803d' },
  { icon: <Shield sx={{ fontSize: 22, color: '#1d4ed8' }} />, label: 'GDPR Compatible', color: '#dbeafe', textColor: '#1d4ed8' },
  { icon: <Key sx={{ fontSize: 22, color: '#7c3aed' }} />, label: 'IT Act 2000', color: '#f3e8ff', textColor: '#7c3aed' },
  { icon: <Lock sx={{ fontSize: 22, color: '#b45309' }} />, label: 'CERT-In Compliant', color: '#fef3c7', textColor: '#b45309' },
];

const SecurityPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout>

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(160deg, #0f172a 0%, #0c1a0c 40%, #0f172a 100%)',
        textAlign: 'center', position: 'relative', overflow: 'hidden',
      }}>
        <Box sx={{ position: 'absolute', top: -200, right: -200, width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(34,197,94,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -100, left: -100, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(220,38,38,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 820, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="Enterprise Security" sx={{ mb: 3, bgcolor: 'rgba(34,197,94,0.15)', color: '#86efac', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(34,197,94,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontSize: { xs: '2.4rem', md: '3.6rem' }, fontWeight: 900,
            color: '#fff', mb: 3, lineHeight: 1.1, letterSpacing: '-0.03em',
          }}>
            Security You Can{' '}
            <Box component="span" sx={{ color: '#4ade80' }}>Trust</Box>
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.2rem' }, color: 'rgba(255,255,255,0.6)', lineHeight: 1.75, mb: 5, maxWidth: 620, mx: 'auto' }}>
            CruvzMail is built with security as the foundation — not an afterthought. Nine layers of protection
            keep your business communications, files, and data safe from threats.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate('/signup')}
              endIcon={<ArrowForward />}
              sx={{
                px: 4, py: 1.75, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '0.95rem',
                bgcolor: '#22c55e', color: '#fff', boxShadow: '0 4px 20px rgba(34,197,94,0.35)',
                '&:hover': { bgcolor: '#16a34a', transform: 'translateY(-2px)', boxShadow: '0 6px 28px rgba(34,197,94,0.45)' }, transition: 'all 0.25s',
              }}
            >
              Start Secure Today
            </Button>
            <Button
              onClick={() => navigate('/data-processing-agreement')}
              sx={{
                px: 4, py: 1.75, borderRadius: '10px', fontWeight: 600, textTransform: 'none', fontSize: '0.95rem',
                color: 'rgba(255,255,255,0.75)', border: '1.5px solid rgba(255,255,255,0.18)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.4)', color: '#fff', bgcolor: 'rgba(255,255,255,0.05)' }, transition: 'all 0.25s',
              }}
            >
              Read our DPA
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── Compliance badges ── */}
      <Box sx={{ py: 4, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: { xs: 2, md: 4 }, flexWrap: 'wrap' }}>
          {certifications.map((c) => (
            <Box key={c.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, py: 1.25, bgcolor: c.color, borderRadius: '10px' }}>
              {c.icon}
              <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: c.textColor }}>{c.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── 9 security layers ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 9 } }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>
              Defense in Depth
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.15 }}>
              9 Layers of Protection
            </Typography>
            <Typography sx={{ fontSize: '1rem', color: '#64748b', mt: 2, maxWidth: 560, mx: 'auto', lineHeight: 1.7 }}>
              Every layer is independently effective — together they form an enterprise-grade security posture.
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {layers.map((layer, i) => (
              <Grid size={{ xs: 12, md: 6 }} key={i}>
                <Box sx={{
                  p: { xs: 3, md: 4 }, border: '1px solid #e2e8f0', borderRadius: '16px',
                  height: '100%', transition: 'all 0.25s', bgcolor: '#fff',
                  '&:hover': { borderColor: '#dc2626', boxShadow: '0 8px 32px rgba(220,38,38,0.08)', transform: 'translateY(-2px)' },
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: '12px', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      {layer.icon}
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#dc2626', letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.25 }}>
                        Layer {String(i + 1).padStart(2, '0')}
                      </Typography>
                      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', lineHeight: 1.25 }}>
                        {layer.title}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: '0.875rem', color: '#64748b', lineHeight: 1.75, mb: 2.5 }}>
                    {layer.desc}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {layer.points.map((p, j) => (
                      <Box key={j} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                        <CheckCircle sx={{ fontSize: 15, color: '#22c55e', mt: 0.35, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#374151', lineHeight: 1.5 }}>{p}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── Email Authentication ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 5, md: 10 }, alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>
              Email Authentication
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.2, mb: 2.5 }}>
              Industry-leading deliverability standards, enforced by default
            </Typography>
            <Typography sx={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, mb: 4 }}>
              Every domain on CruvzMail is automatically configured with the full suite of email authentication
              standards. Your emails arrive in inboxes — not spam — because they're provably from you.
            </Typography>
            <Button onClick={() => navigate('/migration')} endIcon={<ArrowForward />} sx={{ textTransform: 'none', fontWeight: 600, color: '#dc2626', p: 0, fontSize: '0.9rem', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              See how migration works
            </Button>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Grid container spacing={2}>
              {deliverability.map((item) => (
                <Grid size={{ xs: 12, sm: 6 }} key={item.label}>
                  <Box sx={{ p: 2.5, bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: '10px', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography sx={{ fontWeight: 800, fontSize: '0.72rem', color: '#dc2626', letterSpacing: '0.02em' }}>{item.label}</Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>{item.label}</Typography>
                      <Typography sx={{ fontSize: '0.78rem', color: '#64748b' }}>{item.desc}</Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>

      {/* ── Access Control ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#dc2626', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 1.5 }}>
              Access Control
            </Typography>
            <Typography variant="h2" sx={{ fontSize: { xs: '1.9rem', md: '2.5rem' }, fontWeight: 800, color: '#0f172a', letterSpacing: '-0.025em', lineHeight: 1.2 }}>
              You control who gets in
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {[
              {
                icon: <Fingerprint sx={{ fontSize: 28, color: '#dc2626' }} />,
                title: 'Two-Factor Authentication',
                desc: 'Organization admins can enforce 2FA across all users. Supported authenticator apps include Google Authenticator, Microsoft Authenticator, and Authy.',
              },
              {
                icon: <Security sx={{ fontSize: 28, color: '#dc2626' }} />,
                title: 'Role-Based Access Control',
                desc: 'Separate Admin, Domain Admin, and User roles with granular permission enforcement. Admins cannot access user email content — only manage accounts.',
              },
              {
                icon: <Storage sx={{ fontSize: 28, color: '#dc2626' }} />,
                title: 'Session Management',
                desc: 'Admins can view and revoke all active sessions organization-wide. Users can review their own active sessions and terminate unfamiliar ones.',
              },
              {
                icon: <Lock sx={{ fontSize: 28, color: '#dc2626' }} />,
                title: 'Password Policies',
                desc: 'Enforce minimum password length, complexity requirements, and expiry periods. Compromised password detection alerts are sent to affected users.',
              },
            ].map((item, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: '14px', height: '100%', bgcolor: '#fff', transition: 'all 0.2s', '&:hover': { borderColor: '#fca5a5', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' } }}>
                  <Box sx={{ width: 48, height: 48, borderRadius: '10px', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                    {item.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', mb: 1 }}>{item.title}</Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.7 }}>{item.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── Responsible Disclosure ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 780, mx: 'auto', textAlign: 'center' }}>
          <Box sx={{ width: 56, height: 56, borderRadius: '14px', bgcolor: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <BugReport sx={{ fontSize: 28, color: '#dc2626' }} />
          </Box>
          <Typography variant="h3" sx={{ fontSize: { xs: '1.7rem', md: '2.2rem' }, fontWeight: 800, color: '#0f172a', mb: 2.5, letterSpacing: '-0.02em' }}>
            Responsible Disclosure
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', color: '#64748b', lineHeight: 1.8, mb: 4 }}>
            We take security reports seriously and respond promptly. If you discover a vulnerability in CruvzMail,
            please report it privately. We commit to acknowledge within 24 hours, provide regular updates, and
            credit reporters in our security acknowledgements once the issue is resolved.
          </Typography>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, px: 3.5, py: 1.75, bgcolor: '#fff', border: '1.5px solid #e2e8f0', borderRadius: '12px' }}>
            <Security sx={{ fontSize: 18, color: '#dc2626' }} />
            <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#374151' }}>
              security@cruvzmail.com
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{
        py: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        background: 'linear-gradient(135deg, #0f172a 0%, #1e0a0a 100%)',
        textAlign: 'center',
      }}>
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          <Typography variant="h2" sx={{
            fontSize: { xs: '2rem', md: '2.8rem' }, fontWeight: 800,
            color: '#fff', mb: 2.5, lineHeight: 1.15, letterSpacing: '-0.025em',
          }}>
            Your business deserves{' '}
            <Box component="span" sx={{ color: '#4ade80' }}>bulletproof email.</Box>
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.55)', mb: 5, lineHeight: 1.7 }}>
            Start with a free trial. No credit card required. Migration assistance included.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              onClick={() => navigate('/signup')}
              endIcon={<ArrowForward />}
              sx={{
                px: 4.5, py: 1.75, borderRadius: '10px', fontWeight: 700, textTransform: 'none', fontSize: '1rem',
                bgcolor: '#22c55e', color: '#fff', boxShadow: '0 4px 20px rgba(34,197,94,0.35)',
                '&:hover': { bgcolor: '#16a34a', transform: 'translateY(-2px)', boxShadow: '0 8px 32px rgba(34,197,94,0.45)' }, transition: 'all 0.25s',
              }}
            >
              Get Started Free
            </Button>
            <Button
              onClick={() => navigate('/data-processing-agreement')}
              sx={{
                px: 4.5, py: 1.75, borderRadius: '10px', fontWeight: 600, textTransform: 'none', fontSize: '1rem',
                color: 'rgba(255,255,255,0.7)', border: '1.5px solid rgba(255,255,255,0.15)',
                '&:hover': { borderColor: 'rgba(255,255,255,0.35)', color: '#fff' }, transition: 'all 0.2s',
              }}
            >
              View Security Docs
            </Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default SecurityPage;
